import { useRef, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import styles from './mobile-time-picker.module.scss';

type MobileTimePickerProps = {
  value: Dayjs | null;
  onChange: (value: Dayjs) => void;
  disabled?: boolean;
  status?: 'error' | 'warning';
  className?: string;
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

const ITEM_HEIGHT = 36;

type WheelColumnProps = {
  items: (string | number)[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  disabled?: boolean;
};

function WheelColumn({ items, selectedIndex, onSelect, disabled }: WheelColumnProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const initialScrollDone = useRef(false);

  useEffect(() => {
    if (containerRef.current && !isScrollingRef.current) {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
          initialScrollDone.current = true;
        }
      });
    }
  }, [selectedIndex]);

  const handleScroll = () => {
    if (disabled) return;

    isScrollingRef.current = true;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (containerRef.current) {
        const scrollTop = containerRef.current.scrollTop;
        const index = Math.round(scrollTop / ITEM_HEIGHT);
        const clampedIndex = Math.max(0, Math.min(index, items.length - 1));

        containerRef.current.scrollTo({
          top: clampedIndex * ITEM_HEIGHT,
          behavior: 'smooth',
        });

        if (clampedIndex !== selectedIndex) {
          onSelect(clampedIndex);
        }
      }
      isScrollingRef.current = false;
    }, 100);
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.wheelColumn} ${disabled ? styles.disabled : ''}`}
      onScroll={handleScroll}
    >
      <div className={styles.spacer} />
      {items.map((item, index) => (
        <div
          key={index}
          className={`${styles.wheelItem} ${index === selectedIndex ? styles.selected : ''}`}
          onClick={() => !disabled && onSelect(index)}
        >
          {typeof item === 'number' ? String(item).padStart(2, '0') : item}
        </div>
      ))}
      <div className={styles.spacer} />
    </div>
  );
}

export function MobileTimePicker({
  value,
  onChange,
  disabled,
  status,
  className,
}: MobileTimePickerProps) {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [tempValue, setTempValue] = useState<Dayjs | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeValue = tempValue ?? value ?? dayjs();

  // 24-hour clock, as in Poland: the wheel index is the hour itself, so there is no AM/PM
  // column and no 12 <-> 0 conversion to get wrong.
  const hourIndex = activeValue.hour();
  const minuteIndex = activeValue.minute();

  useEffect(() => {
    if (isOpen) {
      setTempValue(value || dayjs());
    } else {
      setTempValue(null);
    }
  }, [isOpen, value]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleHourChange = (index: number) => {
    setTempValue(activeValue.hour(HOURS[index]));
  };

  const handleMinuteChange = (index: number) => {
    setTempValue(activeValue.minute(index));
  };

  const handleConfirm = () => {
    onChange(activeValue);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const displayValue = value ? value.format('HH:mm') : '--:--';

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <button
        type="button"
        className={`${styles.trigger} ${status === 'error' ? styles.error : ''} ${disabled ? styles.disabled : ''}`}
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
      >
        {displayValue}
      </button>

      {isOpen && (
        <div className={styles.overlay} onClick={handleCancel}>
          <div ref={dropdownRef} className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
            <div className={styles.header}>
              <button type="button" className={styles.cancelBtn} onClick={handleCancel}>
                {t('actions.cancel')}
              </button>
              <button type="button" className={styles.confirmBtn} onClick={handleConfirm}>
                {t('actions.ok')}
              </button>
            </div>

            <div className={styles.pickerContainer}>
              <div className={styles.selectionHighlight} />

              <WheelColumn
                items={HOURS}
                selectedIndex={hourIndex}
                onSelect={handleHourChange}
                disabled={disabled}
              />

              <div className={styles.separator}>:</div>

              <WheelColumn
                items={MINUTES}
                selectedIndex={minuteIndex}
                onSelect={handleMinuteChange}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
